import { AdminLayout } from "../../components/layout/AdminLayout";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import { Link2, Plus, Trash2, Key, CheckCircle, AlertCircle, Copy, ToggleLeft, ToggleRight } from "lucide-react";
import api from "../../lib/api";


const LTIManagementPage = () => {
  const { token } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({
    platform_name: "", issuer: "", client_id: "",
    auth_endpoint: "", token_endpoint: "", jwks_url: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/lti/registrations");
      setRegistrations(res.data.registrations || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createRegistration = async () => {
    if (!form.platform_name || !form.issuer || !form.client_id) return toast.error("Platform name, issuer, and client ID are required");
    try {
      const res = await api.post("/lti/registrations", form);
      toast.success(`LTI registration created. Deployment ID: ${res.data.deployment_id}`);
      setShowCreate(false);
      setForm({ platform_name: "", issuer: "", client_id: "", auth_endpoint: "", token_endpoint: "", jwks_url: "" });
      fetchData();
    } catch (e) { toast.error("Failed to create registration"); }
  };

  const deleteRegistration = async (id) => {
    if (!window.confirm("Delete this LTI registration?")) return;
    try {
      await api.delete("/lti/registrations/${id}");
      toast.success("Registration deleted");
      fetchData();
    } catch (e) { toast.error("Failed to delete"); }
  };

  const toggleRegistration = async (id) => {
    try {
      const res = await api.patch("/lti/registrations/${id}/toggle", {});
      toast.success(res.data.message);
      fetchData();
    } catch (e) { toast.error("Failed to toggle"); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`Copied ${label}`);
    setTimeout(() => setCopied(""), 2000);
  };

  if (loading) return <div className="p-6 space-y-4">{[1,2].map(i => <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />)}</div>;

  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  const toolEndpoints = [
    { label: "OIDC Login URL", value: `${baseUrl}/api/lti/login` },
    { label: "Launch Callback URL", value: `${baseUrl}/api/lti/callback` },
    { label: "JWKS URL", value: `${baseUrl}/api/lti/jwks` },
  ];

  return (
    <AdminLayout title="LTI 1.3 Management">
    <div className="p-6 space-y-6" data-testid="lti-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>LTI 1.3 Management</h1>
          <p className="text-sm text-muted-foreground mt-1">Register external learning platforms for LTI 1.3 integration</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="create-lti-btn">
          <Plus className="w-4 h-4 mr-2" /> Register Platform
        </Button>
      </div>

      {/* Tool Configuration Endpoints */}
      <div className="bg-background border rounded-xl p-5 shadow-sm">
        <h3 className="font-bold mb-3 flex items-center gap-2" style={{ fontFamily: "'Sora', sans-serif" }}>
          <Key className="w-4 h-4" /> Tool Configuration (provide to external LMS)
        </h3>
        <div className="space-y-2">
          {toolEndpoints.map(ep => (
            <div key={ep.label} className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2.5">
              <div>
                <span className="text-xs text-muted-foreground">{ep.label}</span>
                <p className="text-sm font-mono">{ep.value}</p>
              </div>
              <button onClick={() => copyToClipboard(ep.value, ep.label)} className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`copy-${ep.label.toLowerCase().replace(/\s/g, '-')}`}>
                {copied === ep.label ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #1B4F8A" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Registrations</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{registrations.length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #0E8A55" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Active</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{registrations.filter(r => r.is_active).length}</p>
        </div>
        <div className="bg-background border rounded-xl p-4" style={{ borderTop: "3px solid #D97706" }}>
          <p className="text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Platforms</p>
          <p className="text-2xl font-extrabold mt-1" style={{ fontFamily: "'Sora', sans-serif" }}>{new Set(registrations.map(r => r.issuer)).size}</p>
        </div>
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowCreate(false)}>
          <div className="bg-background rounded-xl border shadow-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()} data-testid="create-lti-modal">
            <h2 className="text-lg font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Register LTI Platform</h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Platform Name *</label>
                <Input value={form.platform_name} onChange={e => setForm({...form, platform_name: e.target.value})} placeholder="e.g., Canvas, Moodle, Blackboard" className="mt-1" data-testid="lti-platform-name" />
              </div>
              <div>
                <label className="text-sm font-medium">Issuer (iss) *</label>
                <Input value={form.issuer} onChange={e => setForm({...form, issuer: e.target.value})} placeholder="https://canvas.instructure.com" className="mt-1" data-testid="lti-issuer" />
              </div>
              <div>
                <label className="text-sm font-medium">Client ID *</label>
                <Input value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} placeholder="10000000000001" className="mt-1" data-testid="lti-client-id" />
              </div>
              <div>
                <label className="text-sm font-medium">Auth Endpoint</label>
                <Input value={form.auth_endpoint} onChange={e => setForm({...form, auth_endpoint: e.target.value})} placeholder="https://canvas.instructure.com/api/lti/authorize" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium">Token Endpoint</label>
                <Input value={form.token_endpoint} onChange={e => setForm({...form, token_endpoint: e.target.value})} placeholder="https://canvas.instructure.com/login/oauth2/token" className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium">JWKS URL</label>
                <Input value={form.jwks_url} onChange={e => setForm({...form, jwks_url: e.target.value})} placeholder="https://canvas.instructure.com/api/lti/security/jwks" className="mt-1" />
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={createRegistration} data-testid="submit-lti-btn">Register</Button>
            </div>
          </div>
        </div>
      )}

      {/* Registrations Table */}
      <div className="border rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50">
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Platform</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Issuer</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Client ID</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Deployment ID</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Status</th>
              <th className="text-left p-3 text-[10.5px] uppercase font-bold tracking-wider text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                <Link2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="font-medium">No LTI platform registrations</p>
                <p className="text-sm mt-1">Register an external platform to enable LTI 1.3 content sharing</p>
              </td></tr>
            ) : registrations.map(r => (
              <tr key={r.id} className="border-t hover:bg-muted/30 transition-colors" data-testid={`lti-reg-${r.id}`}>
                <td className="p-3 font-medium">{r.platform_name}</td>
                <td className="p-3 text-muted-foreground text-xs font-mono">{r.issuer}</td>
                <td className="p-3"><code className="text-xs bg-muted px-2 py-0.5 rounded">{r.client_id}</code></td>
                <td className="p-3"><code className="text-xs bg-muted px-2 py-0.5 rounded">{r.deployment_id}</code></td>
                <td className="p-3">
                  {r.is_active ? <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase bg-green-100 text-green-800 flex items-center gap-1 w-fit"><CheckCircle className="w-3 h-3" />Active</span>
                    : <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold uppercase bg-red-100 text-red-800 flex items-center gap-1 w-fit"><AlertCircle className="w-3 h-3" />Inactive</span>}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => toggleRegistration(r.id)} className="p-1.5 rounded hover:bg-muted transition-colors" title={r.is_active ? "Deactivate" : "Activate"} data-testid={`toggle-lti-${r.id}`}>
                      {r.is_active ? <ToggleRight className="w-4 h-4 text-green-600" /> : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                    </button>
                    <button onClick={() => deleteRegistration(r.id)} className="p-1.5 rounded hover:bg-red-50 text-red-500 transition-colors" title="Delete" data-testid={`delete-lti-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </AdminLayout>
  );
};

export default LTIManagementPage;
